import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { CommonFlex } from "styledComponents/common";

import arrowIcon from "../../assets/img/icons/dashboard_expand_section_icon.svg";
import {
  OptionArrow,
  OptionLabel,
  OptionSwitcherWrapper,
} from "./optionSwitcherStyles";

const normalizeOptions = options =>
  (options || []).map(option => {
    if (typeof option === "string") {
      return { label: option, value: option };
    }
    return option;
  });

const OptionSwitcher = ({
  options,
  value,
  defaultValue,
  onChange,
  width,
  height,
  minWidth,
  className,
  disabled,
  ...props
}) => {
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const isControlled = value !== undefined && value !== null;

  const [selectedValue, setSelectedValue] = useState(
    isControlled ? value : defaultValue ?? normalizedOptions[0]?.value,
  );

  useEffect(() => {
    if (!isControlled) {
      setSelectedValue(defaultValue ?? normalizedOptions[0]?.value);
    }
  }, [defaultValue, normalizedOptions, isControlled]);

  useEffect(() => {
    if (isControlled) {
      setSelectedValue(value);
    }
  }, [value, isControlled]);

  const selectedOption = normalizedOptions.find(
    option => option.value === selectedValue,
  ) ||
    normalizedOptions[0] || { label: "", value: "" };

  const handleClick = () => {
    if (disabled || normalizedOptions.length === 0) return;

    const currentIndex = normalizedOptions.findIndex(
      option => option.value === selectedOption.value,
    );
    const nextIndex = (currentIndex + 1) % normalizedOptions.length;
    const nextOption = normalizedOptions[nextIndex];

    if (!isControlled) {
      setSelectedValue(nextOption.value);
    }

    if (typeof onChange === "function") {
      onChange(nextOption);
    }
  };

  return (
    <OptionSwitcherWrapper
      width={width}
      height={height}
      minWidth={minWidth}
      className={className}
      onClick={handleClick}
      disabled={disabled}
      {...props}
    >
      {" "}
      <CommonFlex
        style={{ height: "100%" }}
        width="auto"
        alignItems="center"
        justifyContent="center"
        direction="column"
        gap="12px"
      >
        <OptionArrow src={arrowIcon} alt="arrow" />
        <OptionLabel>{selectedOption.label}</OptionLabel>
      </CommonFlex>
    </OptionSwitcherWrapper>
  );
};

OptionSwitcher.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.any.isRequired,
      }),
    ]),
  ),
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  onChange: PropTypes.func,
  width: PropTypes.string,
  height: PropTypes.string,
  minWidth: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

OptionSwitcher.defaultProps = {
  options: [],
  onChange: undefined,
  disabled: false,
};

export default OptionSwitcher;
